import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { DBVoucher } from "@/lib/db-types";
import type { VoucherAllocationRow } from "@/hooks/use-project-detail";

/**
 * VIB-1226/VIB-1204: lettvekts budsjettlinje-shape for godkjenningsdialogen.
 * Bevisst SMALERE enn `BudgetLineRow` (use-project-detail.ts) — panelet
 * trenger verken assignments eller template-metadata, og å hente dem her
 * ville vært en unødvendig tyngre query. Ikke en duplikat-type: ulikt
 * formål (les-only visning vs. full budsjett-editering).
 */
export interface VoucherApprovalBudgetLine {
  id: string;
  phase: string | null;
  description: string | null;
  amount: number | null;
  cost_type: string | null;
  estimated_cost: number | null;
}

export interface VoucherApprovalContext {
  voucher: DBVoucher | null;
  budgetLines: VoucherApprovalBudgetLine[];
  voucherAllocations: VoucherAllocationRow[];
}

type RawAllocationRow = {
  budget_line_id: string | null;
  amount: number | string | null;
  voucher_id: string;
  vouchers: { project_id: string; status: string | null } | null;
};

/**
 * On-demand datahenting for den selvforsynte godkjenningsdialogen
 * (`VoucherApprovalModal`). Henter bilaget selv via `voucherId` (dialogen
 * trenger ikke få hele `DBVoucher` fra kalleren — samme mønster som
 * budsjettlinjene: én kilde, enklest, jf. bølge 3-specen for VIB-1226 §3).
 *
 * `enabled: open` (kalleren styrer via `enabled`-parameteret) — ingen
 * unødvendige kall når dialogen er lukket.
 */
export function useVoucherApprovalContext(
  voucherId: string | null | undefined,
  projectId: string | null | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["voucher-approval-context", projectId, voucherId],
    enabled: enabled && !!voucherId && !!projectId,
    queryFn: async (): Promise<VoucherApprovalContext> => {
      const [voucherRes, budgetRes, allocRes] = await Promise.all([
        supabase.from("vouchers").select("*").eq("id", voucherId!).maybeSingle(),
        supabase
          .from("budget_lines")
          .select("id, phase, description, amount, cost_type, estimated_cost")
          .eq("project_id", projectId!),
        // Samme join-mønster som useProjectDetail: allokeringer for
        // prosjektets bilag, uavhengig av hvilket bilag som godkjennes nå.
        supabase
          .from("voucher_allocations")
          .select("budget_line_id, amount, voucher_id, vouchers!inner(project_id, status)")
          .eq("vouchers.project_id", projectId!),
      ]);

      if (voucherRes.error) throw voucherRes.error;
      if (budgetRes.error) throw budgetRes.error;
      if (allocRes.error) throw allocRes.error;

      const voucherAllocations: VoucherAllocationRow[] = (
        (allocRes.data ?? []) as unknown as RawAllocationRow[]
      ).map((a) => ({
        budget_line_id: a.budget_line_id,
        amount: Number(a.amount ?? 0),
        voucher_id: a.voucher_id,
        voucher_status: a.vouchers?.status ?? null,
      }));

      return {
        voucher: (voucherRes.data as DBVoucher | null) ?? null,
        budgetLines: (budgetRes.data ?? []) as VoucherApprovalBudgetLine[],
        voucherAllocations,
      };
    },
  });
}
